import { useState } from "react";
import { useQuery, useMutation, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import {
  listExtensions,
  extensionType,
  createExtension,
  updateExtension,
  deleteExtension,
  isSafeWebExtensionName,
  UNSAFE_WEB_EXTENSION_NAME_MESSAGE,
  type ExtensionListParams,
  type Extension,
} from "../api/extensions";

const PAGE_SIZE = 50;

// Falls back to the API's own 422 text (app/services/pjsip_naming) when the
// error isn't the client-side check above — e.g. a race with another save,
// or any other save failure this form doesn't otherwise explain.
function readApiError(e: unknown): string {
  const ax = e as { response?: { data?: { detail?: string; error?: string } }; message?: string };
  return ax.response?.data?.detail || ax.response?.data?.error || ax.message || "Something went wrong";
}

function ExtensionEditor({
  ext,
  onClose,
  onSaved,
}: {
  ext: Extension | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState(ext?.name ?? "");
  const [secret, setSecret] = useState("");
  const [title, setTitle] = useState(ext?.title ?? "");
  const [forwardTo, setForwardTo] = useState(ext?.forward_to ?? "");
  const [tech, setTech] = useState<"sip" | "iax" | "web">(
    ext?.is_iax ? "iax" : ext?.is_web ? "web" : "sip",
  );

  // Only WebRTC seats are PJSIP section names — catch a bad one at the point
  // of entry rather than only via an API error toast after Save (beads A3).
  const nameError =
    tech === "web" && name.trim() !== "" && !isSafeWebExtensionName(name)
      ? UNSAFE_WEB_EXTENSION_NAME_MESSAGE
      : null;

  const save = useMutation({
    mutationFn: () => {
      const techFlags = { is_sip: tech === "sip", is_iax: tech === "iax", is_web: tech === "web" };
      if (ext) {
        return updateExtension(ext.extension_id, {
          name,
          title: title || null,
          forward_to: forwardTo || null,
          ...techFlags,
          ...(secret ? { secret } : {}),
        });
      }
      return createExtension({
        name,
        secret,
        title: title || null,
        forward_to: forwardTo || null,
        ...techFlags,
      }).then(() => undefined);
    },
    onSuccess: onSaved,
  });

  return (
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-10 p-4">
      <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md space-y-3">
        <h2 className="text-lg font-bold">{ext ? "Edit" : "New"} extension</h2>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Name / number</label>
          <input
            className={`w-full border rounded px-3 py-2 text-sm ${
              nameError ? "border-red-400" : "border-gray-300"
            }`}
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          {nameError && <p className="text-xs text-red-600 mt-1">{nameError}</p>}
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">
            Secret {ext && <span className="text-gray-400">(leave blank to keep)</span>}
          </label>
          <input
            type="password"
            autoComplete="new-password"
            className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
            value={secret}
            onChange={(e) => setSecret(e.target.value)}
          />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Title</label>
          <input
            className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Forward to (optional)</label>
          <input
            className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
            value={forwardTo}
            onChange={(e) => setForwardTo(e.target.value)}
          />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Technology</label>
          <select
            className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
            value={tech}
            onChange={(e) => setTech(e.target.value as "sip" | "iax" | "web")}
          >
            <option value="sip">SIP</option>
            <option value="iax">IAX2</option>
            <option value="web">WebRTC</option>
          </select>
        </div>
        {save.isError && (
          <p className="text-xs text-red-600">{readApiError(save.error)}</p>
        )}
        <div className="flex gap-2 pt-2">
          <button
            disabled={!name || (!ext && !secret) || !!nameError || save.isPending}
            onClick={() => save.mutate()}
            className="flex-1 bg-brand hover:bg-brand-dark text-white rounded py-2 text-sm disabled:opacity-50"
          >
            {save.isPending ? "Saving…" : "Save"}
          </button>
          <button onClick={onClose} className="flex-1 bg-gray-100 hover:bg-gray-200 rounded py-2 text-sm">
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

export function ExtensionsPage() {
  const [params, setParams] = useState<ExtensionListParams>({
    page: 1,
    per_page: PAGE_SIZE,
    active_only: false,
  });

  const qc = useQueryClient();
  const [editor, setEditor] = useState<Extension | "new" | null>(null);

  const { data, isLoading, isError, error, isFetching } = useQuery({
    queryKey: ["extensions", params],
    queryFn: () => listExtensions(params),
    placeholderData: keepPreviousData,
  });

  const remove = useMutation({
    mutationFn: (id: number) => deleteExtension(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["extensions"] }),
  });

  const page = params.page ?? 1;
  const totalPages = data ? Math.max(1, Math.ceil(data.total / (data.per_page || PAGE_SIZE))) : 1;

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-bold">Extensions</h1>
        <div className="flex items-center gap-4">
          <label className="flex items-center gap-2 text-sm text-gray-600">
            <input
              type="checkbox"
              checked={params.active_only ?? false}
              onChange={(e) =>
                setParams((p) => ({ ...p, active_only: e.target.checked, page: 1 }))
              }
            />
            Active only
          </label>
          <button
            onClick={() => setEditor("new")}
            className="bg-brand hover:bg-brand-dark text-white rounded px-4 py-2 text-sm"
          >
            New extension
          </button>
        </div>
      </div>

      {isLoading ? (
        <p className="text-gray-500">Loading…</p>
      ) : isError ? (
        <p className="text-red-600">{(error as Error).message}</p>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="px-4 py-2 text-xs text-gray-500 border-b border-gray-100">
            {isFetching ? "Loading…" : `${data?.total.toLocaleString() ?? 0} extensions`}
          </div>
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left text-gray-500">
              <tr>
                <th className="px-4 py-2">Name</th>
                <th className="px-4 py-2">Title</th>
                <th className="px-4 py-2">Type</th>
                <th className="px-4 py-2">Agent</th>
                <th className="px-4 py-2">Status</th>
                <th className="px-4 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {(data?.data ?? []).length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-3 text-gray-500">
                    No extensions.
                  </td>
                </tr>
              ) : (
                (data?.data ?? []).map((e) => (
                  <tr key={e.extension_id}>
                    <td className="px-4 py-2 font-medium">{e.name}</td>
                    <td className="px-4 py-2 text-gray-600">{e.title || "—"}</td>
                    <td className="px-4 py-2">{extensionType(e)}</td>
                    <td className="px-4 py-2 text-gray-600">
                      {e.is_logged ? (e.is_paused ? "paused" : "logged in") : "—"}
                    </td>
                    <td className="px-4 py-2">
                      <span
                        className={`px-2 py-0.5 rounded text-xs ${
                          e.is_active ? "bg-green-100 text-green-800" : "bg-gray-200 text-gray-600"
                        }`}
                      >
                        {e.is_active ? "active" : "inactive"}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-right space-x-3">
                      <button onClick={() => setEditor(e)} className="text-brand hover:underline">
                        Edit
                      </button>
                      <button
                        onClick={() => {
                          if (confirm(`Delete extension "${e.name}"?`)) remove.mutate(e.extension_id);
                        }}
                        className="text-red-600 hover:underline"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
          <div className="flex items-center justify-between px-4 py-2 border-t border-gray-100 text-sm">
            <button
              disabled={page <= 1}
              onClick={() => setParams((p) => ({ ...p, page: page - 1 }))}
              className="px-3 py-1 rounded bg-gray-100 hover:bg-gray-200 disabled:opacity-40"
            >
              Previous
            </button>
            <span className="text-gray-500">
              Page {page} of {totalPages}
            </span>
            <button
              disabled={page >= totalPages}
              onClick={() => setParams((p) => ({ ...p, page: page + 1 }))}
              className="px-3 py-1 rounded bg-gray-100 hover:bg-gray-200 disabled:opacity-40"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {editor && (
        <ExtensionEditor
          ext={editor === "new" ? null : editor}
          onClose={() => setEditor(null)}
          onSaved={() => {
            setEditor(null);
            qc.invalidateQueries({ queryKey: ["extensions"] });
          }}
        />
      )}
    </div>
  );
}
